import {
  PATTERNS_LANDING_TEST_QA_OVERRIDE_KEY,
  patternsLandingTestVariants,
  posthogFeatureFlags,
  queryParams
} from '@/shared/constants/constants';
import { initHobbiiStarter } from '@/shared/instances/hobbii-starter';
import { isFeatureFlagEnabled } from '@/shared/pinia/modules/posthog/feature-flags';
import { usePostHogStore } from '@/shared/pinia/modules/posthog/posthog.store';
import { notifyBugsnagWithContext } from '@/shared/utils/error';
import { hobbiiSessionStorage } from '@/shared/utils/web-storage';

const MARKER_SELECTOR = '#template-sections [data-ab-test-flag]';
const SECTION_WRAPPER_SELECTOR =
  "#template-sections > [id^='shopify-section-']";
const REVEALED_ATTRIBUTE = 'data-ab-revealed';

type SwiperInstanceHost = HTMLElement & { swiper?: { update: () => void } };

// Sliders inside test sections initialize while display:none (0 width), so
// revealing the section needs an explicit update - Swiper's own resize
// detection can't be relied on for a hidden-to-visible transition here.
const refreshSlidersIn = (root: ParentNode): void => {
  root.querySelectorAll<SwiperInstanceHost>('.swiper').forEach((el) => {
    el.swiper?.update();
  });
};

const isKnownVariant = (value: string | null): value is string =>
  value === patternsLandingTestVariants.TEST ||
  value === patternsLandingTestVariants.CONTROL;

const getQaOverrideParam = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(queryParams.PATTERNS_LANDING_TEST_VARIANT);
};

export const isABTestFlagEnabled = async (flag: string): Promise<boolean> => {
  try {
    // A QA override is a manual, explicit choice - it should apply even
    // without analytics consent, unlike a real PostHog-sourced flag value,
    // so it's read from its own storage rather than the PostHog flag cache.
    // Only the patterns landing test has one.
    if (flag === posthogFeatureFlags.PATTERNS_LANDING_TEST) {
      const overrideValue = hobbiiSessionStorage.getItem(
        PATTERNS_LANDING_TEST_QA_OVERRIDE_KEY
      );
      if (overrideValue !== null) {
        return isFeatureFlagEnabled(overrideValue);
      }
    }

    await initHobbiiStarter();
    const posthogStore = usePostHogStore();
    const flagValue = await posthogStore.cacheFeatureFlag(flag);
    return isFeatureFlagEnabled(flagValue);
  } catch (error) {
    notifyBugsnagWithContext('isABTestFlagEnabled', error, {
      data: { flag }
    });
    return false;
  }
};

// is being loaded in entrypoint-main.js to persist the QA override on all pages
export const initializePatternsLandingTestQAOverride = (): void => {
  const paramName = queryParams.PATTERNS_LANDING_TEST_VARIANT;

  const paramValue = getQaOverrideParam();
  if (!isKnownVariant(paramValue)) return;

  hobbiiSessionStorage.setItem(
    PATTERNS_LANDING_TEST_QA_OVERRIDE_KEY,
    paramValue
  );

  const newUrl = new URL(window.location.href);
  newUrl.searchParams.delete(paramName);
  window.history.replaceState({}, '', newUrl);
};

const getSectionsByFlag = (): Map<string, HTMLElement[]> => {
  const sectionsByFlag = new Map<string, HTMLElement[]>();
  document
    .querySelectorAll<HTMLTemplateElement>(MARKER_SELECTOR)
    .forEach((marker) => {
      const section = marker.closest<HTMLElement>(SECTION_WRAPPER_SELECTOR);
      if (!section) return;
      const flag = marker.dataset.abTestFlag ?? '';
      sectionsByFlag.set(flag, [...(sectionsByFlag.get(flag) ?? []), section]);
    });
  return sectionsByFlag;
};

// Each flag is resolved once, and only flags used on the page are evaluated,
// so an experiment only logs exposures where its sections actually are.
export const applyABTestSections = async (): Promise<void> => {
  await Promise.all(
    Array.from(getSectionsByFlag(), async ([flag, sections]) => {
      const isEnabled = flag !== '' && (await isABTestFlagEnabled(flag));
      sections.forEach((section) => {
        // Also corrects a pre-paint reveal from a stale cached flag value.
        section.toggleAttribute(REVEALED_ATTRIBUTE, isEnabled);
        if (isEnabled) refreshSlidersIn(section);
      });
    })
  );
};
